import React, { Component } from 'react';
import { UPLOAD_MAX_FILES, UPLOAD_MAX_SIZE, UPLOAD_ALLOWED_TYPES, IMAGE_URL_PREFIX } from '../../constants';

require('./UploadReport.scss');

const staticText = {
  title: 'Upload Report ',
  optional: '(optional)',
  subtext: 'JPEG or PDF (max 10MB)',
  removeAriaLabel: 'Remove file',
  invalidFileType: 'Invalid file type. Only JPEG, PNG, or PDF allowed.',
  uploadFailed: 'Could not upload file.',
};

class UploadReport extends Component {
  constructor(props) {
    super(props);
    this.fileInputRef = null;
    this.state = {
      files: [],
      loading: false,
      error: '',
    };
  }

  /** Called by parent before order creation */
  getFiles() {
    return this.state.files;
  }

  handleFileChange = (e) => {
    const newFiles = e.target.files;
    if (!newFiles || newFiles.length === 0) return;

    const { files } = this.state;
    const remaining = UPLOAD_MAX_FILES - files.length;
    if (remaining <= 0) {
      this.setState({ error: `Maximum ${UPLOAD_MAX_FILES} files allowed.` });
      return;
    }

    this.setState({ loading: true, error: '' });

    const toProcess = Array.from(newFiles).slice(0, remaining);
    const promises = toProcess.map((file) => {
      if (!UPLOAD_ALLOWED_TYPES.includes(file.type)) {
        return Promise.reject(staticText.invalidFileType);
      }
      if (file.size > UPLOAD_MAX_SIZE) {
        return Promise.reject(`${file.name} exceeds the 10MB size limit.`);
      }
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          resolve({
            id: `${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
            file,
            dataUrl: reader.result,
            isPdf: file.type === 'application/pdf',
            type: file.type,
            size: file.size,
            name: file.name,
          });
        };
        reader.readAsDataURL(file);
      });
    });

    const settled = promises.map(promise =>
      promise.then(val => ({ ok: true, val })).catch(err => ({ ok: false, err }))
    );

    Promise.all(settled).then((results) => {
      const newEntries = [];
      let error = '';
      results.forEach(r => {
        if (r.ok) newEntries.push(r.val);
        else error = r.err || staticText.uploadFailed;
      });
      this.setState(prev => ({
        files: [...prev.files, ...newEntries],
        loading: false,
        error,
      }));
    });

    if (this.fileInputRef) this.fileInputRef.value = null;
  }

  removeFile = (id) => {
    this.setState(prev => ({
      files: prev.files.filter(file => file.id !== id),
      error: '',
    }));
  }

  render() {
    const { files, loading, error } = this.state;
    const hasFiles = files.length > 0;
    const canAddMore = files.length < UPLOAD_MAX_FILES;

    return (
      <div className="uploadReportContainer">
        <p className="uploadReportTitle">
          {staticText.title}<span className="uploadReportOptional">{staticText.optional}</span>
        </p>

        <div className="uploadReportContent">
          {canAddMore && (
            <label className={`uploadReportBox${hasFiles ? ' uploadReportBoxSmall' : ''}`}>
              <input
                type="file"
                accept="image/png, image/jpeg, .pdf"
                className="uploadReportInput"
                onChange={this.handleFileChange}
                ref={r => { this.fileInputRef = r; }}
                multiple
              />
              <div className="uploadReportBoxContent">
                <span className="uploadReportPlus">+</span>
                {!hasFiles && (
                  <p className="uploadReportSubtext">{staticText.subtext}</p>
                )}
              </div>
            </label>
          )}

          {files.map(file => (
            <div key={file.id} className="uploadReportThumb">
              <button
                type="button"
                className="uploadReportRemove"
                onClick={() => this.removeFile(file.id)}
                aria-label={staticText.removeAriaLabel}
              >
                &times;
              </button>
              <img
                src={file.isPdf ? `${IMAGE_URL_PREFIX}pdfIcon.png` : file.dataUrl}
                alt={file.name}
                className="uploadReportThumbImg"
              />
            </div>
          ))}
        </div>

        {loading && (
          <div className="uploadReportLoader">
            <div className="uploadReportSpinner" />
          </div>
        )}

        {error && <p className="uploadReportError">{error}</p>}
      </div>
    );
  }
}

export default UploadReport;
